import type { OpenClaimLineItem } from "@freightsimple/generated-dashboard-openapi-client/models/OpenClaimLineItem";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { FreightClaimType } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimType";
import { ClaimsDamageClaimLineItemRow } from "../CustomComponents/ClaimsDamageClaimLineItemRow";
import { ClaimsSubmitClaimSection } from "./ClaimsSubmitClaimSection";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { BulletList } from "@freightsimple/ui-core/Typography/BulletList";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

interface DamageClaimLineItemsSectionProps {
  index: number;
  shipment: Shipment;
  currency: Currency;
  claimLineItems: OpenClaimLineItem[];
  setClaimLineItems: (_: OpenClaimLineItem[]) => void;
  forceValidation: boolean;
  disabled?: boolean | undefined;
}

export function ClaimsDamageClaimLineItemsSection(
  props: DamageClaimLineItemsSectionProps,
) {
  const { currency, claimLineItems } = props;
  const nothingClaimed = claimLineItems.every(
    (o) => o.claimType === FreightClaimType.NoDamageOrLoss,
  );
  const hasDamageClaim = claimLineItems.some(
    (item) => item.claimType === FreightClaimType.Damage,
  );

  return (
    <ClaimsSubmitClaimSection
      index={props.index}
      title="Lost or Damaged Line Items"
      description="Please tell us which line items of your shipment suffered damage or loss"
      content={
        <Stack gap={4} align="stretch">
          {hasDamageClaim && (
            <Banner
              variant="info"
              hideIcon
              title="When a shipment is damaged, we require supporting documentation:"
            >
              <BulletList>
                <li>
                  <b>Repairable shipments:</b> A repair quote from a qualified
                  technician specifying the repair costs.
                </li>
                <li>
                  <b>Non-repairable shipments:</b> A statement from the
                  manufacturer or a certified third-party specialist confirming
                  the item cannot be repaired.
                </li>
              </BulletList>
            </Banner>
          )}

          {props.shipment.lineItems.map(function (lineItem, index) {
            const claimLineItem = claimLineItems[index];
            if (claimLineItem === undefined) {
              return <></>;
            }
            return (
              <ClaimsDamageClaimLineItemRow
                key={`${lineItem.description}-${lineItem.length}x${lineItem.width}x${lineItem.height}-${lineItem.weightPerHandlingUnit}-${lineItem.numberHandlingUnits}`}
                lineItem={lineItem}
                index={index}
                currency={currency}
                claimLineItem={claimLineItem}
                setClaimLineItem={function (updatedClaimLineItem) {
                  claimLineItems[index] = updatedClaimLineItem;
                  // do the ... to force a new array and cause react to rerender
                  props.setClaimLineItems([...claimLineItems]);
                }}
                forceValidation={props.forceValidation}
                disabled={props.disabled}
              />
            );
          })}

          {props.forceValidation && nothingClaimed && (
            <Banner
              variant="danger"
              title="No damage or loss has been indicated"
            >
              Set at least one line item to Damage or Loss to submit a claim.
            </Banner>
          )}
        </Stack>
      }
    />
  );
}
